/**
 * DeliberationsReferentielsPage — Paramètres → Délibérations (D0, mig 267).
 *
 * ─── CE QUE CET ÉCRAN EST ───────────────────────────────────────────────────
 *
 * Le pendant, côté conseil, de ce qu'est « Signataire des actes » pour les
 * arrêtés : ce qui se règle UNE FOIS et sert partout ensuite.
 *
 * Deux blocs, et rien de plus tant que D1 n'existe pas :
 *
 *   · le CONSEIL MUNICIPAL — nom, qualité, rang au tableau, mandat ;
 *   · les SUJETS — le référentiel fermé qui construira la bibliothèque
 *     « un sujet → toutes ses délibérations ».
 *
 * ─── POURQUOI ON DÉSACTIVE AU LIEU DE SUPPRIMER ─────────────────────────────
 *
 * Un sujet retiré doit rester lisible sur les délibérations passées. Le
 * supprimer casserait la bibliothèque au moment précis où elle sert : quand on
 * cherche une délibération de 2021 sur un sujet qu'on ne nomme plus pareil.
 */

import { useCallback, useEffect, useState } from 'react';
import { Card, Alert, Button, Badge, ModuleHeader } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import {
  Landmark, Plus, Loader2, AlertCircle, Info, Eye, EyeOff, Users, Tags, Paperclip, Trash2,
} from 'lucide-react';

interface Matiere { code: string; libelle: string; ordre: number }
interface Sujet {
  id: string; code: string | null; libelle: string;
  matiereCode: string | null; matiereLibelle: string | null;
  ordre: number; actif: boolean;
}
interface Attendue {
  id: string; code: string; libelle: string;
  obligatoire: boolean; ordre: number; actif: boolean;
}
interface Conseiller {
  id: string; userId: string | null; nom: string; prenom: string;
  qualite: 'maire' | 'adjoint' | 'conseiller';
  rang: number | null; dateDebut: string | null; dateFin: string | null;
  actif: boolean;
}

const QUALITES: Record<Conseiller['qualite'], string> = {
  maire: 'Maire', adjoint: 'Adjoint', conseiller: 'Conseiller municipal',
};

const champ =
  'h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink';
const label =
  'mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle';

export function DeliberationsReferentielsPage() {
  // Jamais window.confirm : incompatible Capacitor + UX vitrée (charte projet).
  const { confirm, dialogElement } = useConfirmDialog();
  const [matieres, setMatieres] = useState<Matiere[]>([]);
  const [sujets, setSujets] = useState<Sujet[]>([]);
  const [conseillers, setConseillers] = useState<Conseiller[]>([]);
  const [attendues, setAttendues] = useState<Attendue[]>([]);
  const [aLibelle, setALibelle] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // Saisie d'un sujet
  const [sLibelle, setSLibelle] = useState('');
  const [sCode, setSCode] = useState('');
  const [sMatiere, setSMatiere] = useState('');

  // Saisie d'un conseiller
  const [cNom, setCNom] = useState('');
  const [cPrenom, setCPrenom] = useState('');
  const [cQualite, setCQualite] = useState<Conseiller['qualite']>('conseiller');
  const [cRang, setCRang] = useState('');

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // `tous=true` : les valeurs désactivées restent affichées, sinon on ne
      // peut pas les réactiver.
      const [r, ra] = await Promise.all([
        api.deliberations.referentiels(true),
        api.deliberations.piecesAttendues(),
      ]);
      setMatieres(r.matieres); setSujets(r.sujets); setConseillers(r.conseillers);
      setAttendues(ra.attendues);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  async function ajouterSujet() {
    if (sLibelle.trim().length < 2) { setError('Libellé trop court'); return; }
    setSaving(true); setError(null); setMsg(null);
    try {
      await api.deliberations.creerSujet({
        libelle: sLibelle.trim(),
        code: sCode.trim() || null,
        matiereCode: sMatiere || null,
      });
      setSLibelle(''); setSCode(''); setSMatiere('');
      setMsg('Sujet ajouté — il est désormais proposé à la rédaction.');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Ajout impossible');
    } finally { setSaving(false); }
  }

  async function ajouterConseiller() {
    if (cNom.trim().length < 1 || cPrenom.trim().length < 1) {
      setError('Nom et prénom sont attendus'); return;
    }
    setSaving(true); setError(null); setMsg(null);
    try {
      await api.deliberations.creerConseiller({
        nom: cNom.trim(), prenom: cPrenom.trim(), qualite: cQualite,
        rang: cRang ? Number(cRang) : null,
      });
      setCNom(''); setCPrenom(''); setCRang(''); setCQualite('conseiller');
      setMsg('Conseiller enregistré.');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Ajout impossible');
    } finally { setSaving(false); }
  }

  async function basculerSujet(s: Sujet) {
    setError(null);
    try {
      await api.deliberations.majSujet(s.id, { actif: !s.actif });
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Modification impossible');
    }
  }

  /**
   * Suppression d'un sujet créé par erreur (demande du 29/07).
   *
   * Le serveur seul sait si le sujet a déjà servi : il renvoie 409
   * `SUJET_UTILISE` avec le nombre de délibérations rattachées. On affiche ce
   * message tel quel — il dit déjà quoi faire à la place (désactiver).
   */
  async function supprimerSujet(s: Sujet) {
    const ok = await confirm({
      title: 'Supprimer ce sujet ?',
      description: `« ${s.libelle} » disparaîtra du référentiel. La suppression n’est possible que si aucune délibération ne s’y rattache — sinon il faudra le désactiver.`,
      danger: true,
      confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    setError(null);
    try {
      await api.deliberations.supprimerSujet(s.id);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Suppression impossible');
    }
  }

  async function basculerConseiller(c: Conseiller) {
    setError(null);
    try {
      await api.deliberations.majConseiller(c.id, { actif: !c.actif });
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Modification impossible');
    }
  }

  async function ajouterAttendue() {
    if (aLibelle.trim().length < 2) { setError('Libellé trop court'); return; }
    setSaving(true); setError(null); setMsg(null);
    try {
      await api.deliberations.creerPieceAttendue({ libelle: aLibelle.trim() });
      setALibelle(''); setMsg('Pièce ajoutée à la liste attendue.');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Ajout impossible');
    } finally { setSaving(false); }
  }

  async function basculerAttendue(a: Attendue) {
    setError(null);
    try {
      await api.deliberations.majPieceAttendue(a.id, { actif: !a.actif });
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Modification impossible');
    }
  }

  return (
    // Pas d'`AppHeader` ici : `AdminLayout` en pose déjà un. Il y en avait deux
    // empilés dans le paramétrage des délibérations — remonté par Henrico le
    // 29/07 sur la capture de /admin/deliberations. Les autres pages de ce
    // menu (LieuxPage…) n'en posent aucun : c'est la règle du gabarit admin.
    <div className="min-h-screen text-ink">
      {dialogElement}
      <ModuleHeader
        accent="grc" icon={Landmark}
        eyebrow="Paramètres" title="Délibérations"
      />

      <main id="main-content" className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8 space-y-4">
        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
        {msg && <Alert variant="success">{msg}</Alert>}

        <Alert variant="info" icon={Info}>
          Ce qui se règle ici sert partout ensuite : le conseil municipal et les
          sujets sont <strong>choisis</strong> à la rédaction, jamais retapés.
          C'est ce qui évite de retrouver le même sujet écrit de quatre façons.
        </Alert>

        {loading ? (
          <Card variant="glass" padded>
            <p className="flex items-center gap-2 text-sm text-ink-muted">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Chargement…
            </p>
          </Card>
        ) : (
          <>
            {/* ══ Le conseil municipal ══ */}
            <Card variant="glass" padded>
              <h2 className="flex items-center gap-2 font-medium text-ink">
                <Users className="h-4 w-4 text-grc" aria-hidden="true" />
                Conseil municipal
              </h2>
              <p className="mt-1 text-sm text-ink-muted">
                Nom, qualité et mandat — rien d'autre. Les votes sont enregistrés
                en décompte, jamais nominativement.
              </p>

              <div className="mt-3 grid gap-3 rounded-r-md border border-border bg-surface/40 p-3 sm:grid-cols-4">
                <div>
                  <label className={label} htmlFor="c-prenom">Prénom</label>
                  <input id="c-prenom" value={cPrenom}
                    onChange={(e) => setCPrenom(e.target.value)} className={champ} />
                </div>
                <div>
                  <label className={label} htmlFor="c-nom">Nom</label>
                  <input id="c-nom" value={cNom}
                    onChange={(e) => setCNom(e.target.value)} className={champ} />
                </div>
                <div>
                  <label className={label} htmlFor="c-qualite">Qualité</label>
                  <select id="c-qualite" value={cQualite}
                    onChange={(e) => setCQualite(e.target.value as Conseiller['qualite'])}
                    className={champ}>
                    {(Object.keys(QUALITES) as Conseiller['qualite'][]).map((q) => (
                      <option key={q} value={q}>{QUALITES[q]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={label} htmlFor="c-rang">Rang au tableau</label>
                  <input id="c-rang" type="number" min={1} max={99} value={cRang}
                    onChange={(e) => setCRang(e.target.value)} className={champ} />
                  {/* Le rang n'est pas décoratif : l'ordre des adjoints
                      détermine qui supplée le maire (CGCT L. 2122-17). */}
                  <p className="mt-1 text-xs text-ink-subtle">
                    L'ordre des adjoints détermine qui supplée le maire.
                  </p>
                </div>
                <div className="sm:col-span-4">
                  <Button variant="primary" size="sm" loading={saving}
                    onClick={() => void ajouterConseiller()}>
                    <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter au conseil
                  </Button>
                </div>
              </div>

              {conseillers.length === 0 ? (
                <p className="py-6 text-center text-sm text-ink-muted">
                  Le conseil n'est pas encore saisi.
                </p>
              ) : (
                <ul className="mt-3 divide-y divide-border">
                  {conseillers.map((c) => (
                    <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                      <span className="min-w-0">
                        <span className={`text-sm text-ink ${c.actif ? '' : 'line-through opacity-60'}`}>
                          {c.prenom} {c.nom}
                        </span>
                        <span className="ml-2">
                          <Badge variant="neutral" size="sm">{QUALITES[c.qualite]}</Badge>
                        </span>
                        {c.rang !== null && (
                          <span className="ml-2 text-2xs text-ink-subtle">rang {c.rang}</span>
                        )}
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => void basculerConseiller(c)}>
                        {c.actif
                          ? <><EyeOff className="h-4 w-4" aria-hidden="true" /> Clôturer le mandat</>
                          : <><Eye className="h-4 w-4" aria-hidden="true" /> Réactiver</>}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* ══ Les sujets ══ */}
            <Card variant="glass" padded>
              <h2 className="flex items-center gap-2 font-medium text-ink">
                <Tags className="h-4 w-4 text-grc" aria-hidden="true" />
                Sujets de délibération
              </h2>
              <p className="mt-1 text-sm text-ink-muted">
                Vous les créez ici ; ailleurs, on les choisit. La matière est la
                nomenclature nationale du contrôle de légalité — elle sera
                proposée quand le sujet est retenu, jamais imposée.
              </p>

              <div className="mt-3 grid gap-3 rounded-r-md border border-border bg-surface/40 p-3 sm:grid-cols-4">
                <div className="sm:col-span-2">
                  <label className={label} htmlFor="s-libelle">Nouveau sujet</label>
                  <input id="s-libelle" value={sLibelle}
                    onChange={(e) => setSLibelle(e.target.value)} className={champ}
                    placeholder="Journée des associations" />
                </div>
                <div>
                  <label className={label} htmlFor="s-code">Code</label>
                  <input id="s-code" value={sCode}
                    onChange={(e) => setSCode(e.target.value)} className={champ}
                    placeholder="facultatif" />
                </div>
                <div>
                  <label className={label} htmlFor="s-matiere">Matière proposée</label>
                  <select id="s-matiere" value={sMatiere}
                    onChange={(e) => setSMatiere(e.target.value)} className={champ}>
                    <option value="">—</option>
                    {matieres.map((m) => (
                      <option key={m.code} value={m.code}>{m.code}. {m.libelle}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-4">
                  <Button variant="primary" size="sm" loading={saving}
                    onClick={() => void ajouterSujet()}>
                    <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter le sujet
                  </Button>
                </div>
              </div>

              {sujets.length === 0 ? (
                <p className="py-6 text-center text-sm text-ink-muted">
                  Aucun sujet. Commencez par ceux qui reviennent le plus souvent
                  en conseil.
                </p>
              ) : (
                <ul className="mt-3 divide-y divide-border">
                  {sujets.map((s) => (
                    <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                      <span className="min-w-0">
                        <span className={`text-sm text-ink ${s.actif ? '' : 'line-through opacity-60'}`}>
                          {s.code ? `${s.code} — ` : ''}{s.libelle}
                        </span>
                        {s.matiereLibelle && (
                          <span className="ml-2 text-2xs text-ink-subtle">
                            matière {s.matiereCode} · {s.matiereLibelle}
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => void basculerSujet(s)}>
                          {s.actif
                            ? <><EyeOff className="h-4 w-4" aria-hidden="true" /> Désactiver</>
                            : <><Eye className="h-4 w-4" aria-hidden="true" /> Réactiver</>}
                        </Button>
                        {/* Réparer une faute de saisie. Le serveur refuse si le
                            sujet a déjà servi — le message le dit alors. */}
                        <Button variant="ghost" size="sm" onClick={() => void supprimerSujet(s)}
                          aria-label={`Supprimer le sujet ${s.libelle}`}>
                          <Trash2 className="h-4 w-4" aria-hidden="true" /> Supprimer
                        </Button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <p className="mt-3 flex items-start gap-2 text-xs text-ink-subtle">
                <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                Un sujet se désactive, il ne se supprime pas : il doit rester
                lisible sur les délibérations passées qui s'y rattachent.
              </p>
            </Card>

            {/* ══ Les pièces attendues (D2-02) ══ */}
            <Card variant="glass" padded>
              <h2 className="flex items-center gap-2 font-medium text-ink">
                <Paperclip className="h-4 w-4 text-grc" aria-hidden="true" />
                Pièces attendues sur une délibération
              </h2>
              <p className="mt-1 text-sm text-ink-muted">
                Ce sont ces lignes que l'écran affiche en face de chaque
                délibération — remplies ou <strong>manquantes</strong>. Les cinq
                de la procédure sont posées d'office ; adaptez-les à vos usages.
              </p>

              <div className="mt-3 grid gap-3 rounded-r-md border border-border bg-surface/40 p-3 sm:grid-cols-4">
                <div className="sm:col-span-3">
                  <label className={label} htmlFor="a-libelle">Nouvelle pièce attendue</label>
                  <input id="a-libelle" value={aLibelle}
                    onChange={(e) => setALibelle(e.target.value)} className={champ}
                    placeholder="Avis de la commission des finances" />
                </div>
                <div className="flex items-end">
                  <Button variant="primary" size="sm" loading={saving}
                    onClick={() => void ajouterAttendue()}>
                    <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter
                  </Button>
                </div>
              </div>

              <ul className="mt-3 divide-y divide-border">
                {attendues.map((a) => (
                  <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <span className={`text-sm text-ink ${a.actif ? '' : 'line-through opacity-60'}`}>
                      {a.libelle}
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => void basculerAttendue(a)}>
                      {a.actif
                        ? <><EyeOff className="h-4 w-4" aria-hidden="true" /> Désactiver</>
                        : <><Eye className="h-4 w-4" aria-hidden="true" /> Réactiver</>}
                    </Button>
                  </li>
                ))}
              </ul>

              <p className="mt-3 flex items-start gap-2 text-xs text-ink-subtle">
                <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                Désactiver une ligne ne supprime rien : les fichiers déjà déposés
                dessous restent accessibles sous « Autres pièces ».
              </p>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
